import type { InvoiceStatus, MonthlyCostPoint, ProjectDashboard } from '@faktorplus/shared';
import { INVOICE_STATUS_LABELS } from '@faktorplus/shared';
import {
  formatMoney,
  formatMoneyCompact,
  formatMonthLong,
  formatMonthShort,
  plural,
} from '../../lib/format';
import styles from './projects.module.css';

/**
 * The site screen's charts.
 *
 * Every one of them draws a single measure — cost in złoty — so they all use
 * one hue and get their identity from the label beside the mark rather than
 * from colour. The only coloured chart is the workflow bar, where colour means
 * a status the rest of the app already paints the same way, and even there the
 * legend spells the status out.
 */

/* ─────────────────────────── Share of the largest ───────────────────────── */

/** A row-level bar for the site list: how this site compares with the biggest. */
export function CostShareBar({
  amount,
  largest,
  label,
}: {
  amount: number;
  largest: number;
  label: string;
}) {
  const share = Math.max(0, Math.min(1, largest === 0 ? 0 : amount / largest));

  return (
    <span
      className={styles.shareTrack}
      role="img"
      aria-label={`${label}: ${formatMoney(amount)}`}
      title={`${label} — ${formatMoney(amount)}`}
    >
      <span className={styles.shareFill} style={{ width: `${share * 100}%` }} />
    </span>
  );
}

/* ───────────────────────────── Ranked cost bars ─────────────────────────── */

export interface RankedItem {
  key: string;
  label: string;
  amount: number;
  /** Small print under the bar — usually the invoice count. */
  note?: string;
}

/**
 * Ranked horizontal bars — the form for "which of these cost the most".
 *
 * Each bar carries its own amount and share, so the reader never has to
 * measure a wedge against a legend to answer the question.
 */
export function RankedCostBars({ items, total }: { items: RankedItem[]; total: number }) {
  const largest = Math.max(...items.map((item) => item.amount), 1);

  return (
    <ul className={styles.rankedList}>
      {items.map((item) => (
        <li key={item.key} className={styles.rankedRow}>
          <div className={styles.rankedHead}>
            <span className={styles.rankedLabel}>{item.label}</span>
            <span className={styles.rankedValue}>
              {formatMoneyCompact(item.amount)}
              {total > 0 && (
                <span className={styles.rankedShare}> · {formatShare(item.amount / total)}</span>
              )}
            </span>
          </div>
          <span className={styles.barTrack} aria-hidden="true">
            <span
              className={styles.barFill}
              style={{ width: `${(item.amount / largest) * 100}%` }}
            />
          </span>
          {item.note && <span className={styles.rankedNote}>{item.note}</span>}
        </li>
      ))}
    </ul>
  );
}

/* ────────────────────────────── Monthly trend ───────────────────────────── */

/**
 * Cost per month of issue.
 *
 * Only the peak is labelled: a number over every column turns a shape you can
 * read at a glance back into a table you have to read row by row.
 */
export function MonthlyCostChart({ points }: { points: MonthlyCostPoint[] }) {
  const largest = Math.max(...points.map((point) => point.netAmount), 1);
  const peak = points.reduce(
    (highest, point) => (point.netAmount > highest.netAmount ? point : highest),
    points[0]!,
  );

  return (
    <ul className={styles.columns}>
      {points.map((point) => (
        <li
          key={point.month}
          className={styles.column}
          title={describeMonth(point)}
          aria-label={describeMonth(point)}
        >
          <span className={styles.columnPeak}>
            {point.month === peak.month && point.netAmount > 0
              ? formatMoneyCompact(point.netAmount)
              : ''}
          </span>
          <span className={styles.columnTrack} aria-hidden="true">
            <span
              className={styles.columnFill}
              style={{ height: `${(point.netAmount / largest) * 100}%` }}
            />
          </span>
          <span className={styles.columnLabel} aria-hidden="true">
            {formatMonthShort(point.month)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function describeMonth(point: MonthlyCostPoint): string {
  const invoices = `${point.invoiceCount} ${plural(point.invoiceCount, ['faktura', 'faktury', 'faktur'])}`;
  return `${formatMonthLong(point.month)} — ${formatMoney(point.netAmount)} netto, ${invoices}`;
}

/* ──────────────────────────────── Budget ────────────────────────────────── */

/**
 * Contract value against what has been booked.
 *
 * A single fact with a threshold, so it is a meter rather than a chart. Past
 * 100% the fill turns critical, because that is the only reading that changes
 * what anybody does about it.
 */
export function BudgetMeter({ budget }: { budget: NonNullable<ProjectDashboard['budget']> }) {
  const share = Math.max(0, budget.usedShare);
  const isOverrun = share > 1;

  return (
    <div>
      <div className={styles.meterHead}>
        <span className={styles.meterValue}>{formatShare(share)}</span>
        <span className={styles.meterNote}>
          {formatMoney(budget.netAmount)} z {formatMoney(budget.contractValue)}
        </span>
      </div>

      <span
        className={styles.meterTrack}
        role="img"
        aria-label={`Wykorzystanie kontraktu: ${formatShare(share)}`}
      >
        <span
          className={isOverrun ? styles.meterFillOverrun : styles.meterFill}
          style={{ width: `${Math.min(share, 1) * 100}%` }}
        />
      </span>

      <p className={styles.meterCaption}>
        {isOverrun
          ? `Przekroczenie o ${formatMoney(budget.netAmount - budget.contractValue)} netto.`
          : `Pozostało ${formatMoney(budget.contractValue - budget.netAmount)} netto.`}
      </p>
    </div>
  );
}

/* ─────────────────────────────── Workflow bar ───────────────────────────── */

/** Status colour is semantic here, exactly as it is on the status pills. */
const STATUS_SEGMENT: Record<InvoiceStatus, string> = {
  NEW: styles.segmentNeutral!,
  ASSIGNED: styles.segmentWaiting!,
  APPROVED_BY_MANAGER: styles.segmentAccent!,
  DISPUTED: styles.segmentCritical!,
  APPROVED_FOR_PAYMENT: styles.segmentAccent2!,
  PAID: styles.segmentOk!,
};

/** Where the site's invoices are sitting right now, by count. */
export function StatusPipeline({ entries }: { entries: ProjectDashboard['byStatus'] }) {
  const total = entries.reduce((sum, entry) => sum + entry.count, 0);
  if (total === 0) return null;

  return (
    <div>
      <span className={styles.pipelineTrack} aria-hidden="true">
        {entries.map((entry) => (
          <span
            key={entry.status}
            className={`${styles.pipelineSegment} ${STATUS_SEGMENT[entry.status]}`}
            style={{ width: `${(entry.count / total) * 100}%` }}
          />
        ))}
      </span>

      <ul className={styles.pipelineLegend}>
        {entries.map((entry) => (
          <li key={entry.status} className={styles.pipelineItem}>
            <span
              className={`${styles.pipelineSwatch} ${STATUS_SEGMENT[entry.status]}`}
              aria-hidden="true"
            />
            <span className={styles.pipelineLabel}>{INVOICE_STATUS_LABELS[entry.status]}</span>
            {/* Separated on purpose: "24" next to "415 557 zł" reads as one
                number if you let the two columns touch. */}
            <span className={styles.pipelineCount}>{entry.count}</span>
            <span className={styles.pipelineAmount}>· {formatMoneyCompact(entry.netAmount)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A cost that rounds to zero percent is not zero. Saying "0%" next to
 * "1 974 zł" reads as a bug, so anything that small is shown as "<1%".
 */
function formatShare(share: number): string {
  if (share > 0 && share < 0.005) return '<1%';
  return `${Math.round(share * 100)}%`;
}
